import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionHead from '../shared/SectionHead';
import Icon from '../shared/Icon';
import { useReveal } from '../../hooks/useReveal';
import useLocalizedPath from '../../hooks/useLocalizedPath';

function ProductCard({ tag, name, desc, img, href, bullets, kbd, learnMore }) {
  return (
    <Link
      to={href}
      className="reveal"
      style={{
        display: 'flex', flexDirection: 'column',
        background: 'var(--p2a-dark-panel)',
        border: '1px solid var(--p2a-dark-border)',
        borderRadius: 6,
        overflow: 'hidden',
        transition: 'border-color var(--p2a-dur-base) var(--p2a-ease), transform var(--p2a-dur-base) var(--p2a-ease)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--p2a-green-500)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--p2a-dark-border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ position: 'relative', aspectRatio: '16/9', background: `url("${img}") center/cover` }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,7,10,0.15), rgba(5,7,10,0.75))' }} />
        <div style={{
          position: 'absolute', top: 16, left: 16,
          fontFamily: 'var(--p2a-font-mono)',
          fontSize: 11, letterSpacing: '0.14em',
          color: 'var(--p2a-green-400)',
          padding: '4px 10px',
          background: 'rgba(5,7,10,0.7)',
          border: '1px solid var(--p2a-dark-border-2)',
          borderRadius: 4,
        }}>{tag}</div>

        <div style={{
          position: 'absolute', top: 16, right: 16,
          width: 48, height: 48,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--p2a-green-500)',
          color: 'var(--p2a-green-400)',
          fontFamily: 'var(--p2a-font-mono)',
          fontSize: 18, fontWeight: 600,
          background: 'rgba(5,7,10,0.5)',
          borderRadius: 4,
        }}>{kbd}</div>
      </div>

      <div style={{ padding: '28px 28px 32px' }}>
        <h3 className="h-2" style={{ marginBottom: 12 }}>{name}</h3>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--p2a-dark-fg-3)' }}>{desc}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: '22px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, fontSize: 13.5, color: 'var(--p2a-dark-fg-2)' }}>
              <span style={{ color: 'var(--p2a-green-500)', flexShrink: 0 }}>✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 28 }}>
          <span className="arrow-link">
            {learnMore} <Icon.ArrowRight className="arrow" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ProductsDuo() {
  const { t } = useTranslation();
  const ref = useReveal({ stagger: 0.1 });
  const lp = useLocalizedPath();
  const learnMore = t('home.products.learn_more');

  return (
    <section
      ref={ref}
      className="section"
      style={{ background: 'var(--p2a-dark-bg)', borderBottom: '1px solid var(--p2a-dark-border)' }}
    >
      <div className="p2-container">
        <SectionHead
          eyebrow={t('home.products.eyebrow')}
          title={t('home.products.title')}
          lede={t('home.products.lede')}
          max={640}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 56 }} className="products-grid">
          <ProductCard
            tag={t('home.products.pixrow.tag')}
            name="PixRow"
            desc={t('home.products.pixrow.desc')}
            img="/assets/linhas.webp"
            href={lp('/pixrow')}
            bullets={t('home.products.pixrow.bullets', { returnObjects: true })}
            kbd="PR"
            learnMore={learnMore}
          />
          <ProductCard
            tag={t('home.products.pixviewer.tag')}
            name="PixViewer"
            desc={t('home.products.pixviewer.desc')}
            img="/assets/pixviewer_gaps.webp"
            href={lp('/pixviewer')}
            bullets={t('home.products.pixviewer.bullets', { returnObjects: true })}
            kbd="PV"
            learnMore={learnMore}
          />
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .products-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
